import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  GetInspectionType,
  GetInspectionItem,
  MarkerInfo,
  Status,
  PutInspectionType,
  InspectionType,
  PutInspectionItem,
} from '../../services/apiTypes';
import {
  fetchInspectionTypeData as fetchTypeDataAPI,
  updateInspectionTypeData as updateTypeDataAPI,
  GetInspectionTypeParam,
  fetchQcType,
  fetchQcTypeAll as fetchQcTypeAllAPI,
  GetQcTypeParam,
  updateQcType,
  GetQcTypeAllParam,
} from './api';
import {
  findInspectionItem,
  getStatusInspectionOfType,
  isNa,
  isOk,
} from '../../_utils/inspectionUtils';
import { DTA_IMAGE_INSPECTION } from '../../_constants/inspectionConst';
import {
  isGetInspectionType,
  isGetQcType,
  isGetQcTypeList,
} from '../../_utils/typeGuardUtils';
import { RejectedTypeList } from '../../pages/Inspection/Inspection';
import { INSPECTION_DISPLAY_TYPE } from '../../_constants/manageConst';

export type InspectionTypeDataMap = {
  [key: string]: { [key: string]: InspectionType };
};

export type QcTypeDataMap = {
  [key: string]: { [key: string]: GetInspectionType };
};

interface InspectionTypeDataState {
  status: Status;
  inspectionTypeDataMap: InspectionTypeDataMap;
  before: QcTypeDataMap;
  isAllOk: boolean;
}

const initialState: InspectionTypeDataState = {
  status: 'idle',
  inspectionTypeDataMap: {},
  before: {},
  isAllOk: false,
};

const convertPutData = (data: InspectionType): PutInspectionType => {
  const { status, manualPath, ngSample, ...rest } = data;

  const convertItemData = (items: GetInspectionItem[]): PutInspectionItem[] => {
    return items.map((item) => {
      const { id, judgement, numericalValue, memo, itemData } = item;
      return {
        id,
        judgement,
        numericalValue,
        memo,
        itemData: convertItemData(itemData),
      };
    });
  };

  const putItemList: PutInspectionItem[] = convertItemData(rest.itemData);

  return { ...rest, itemData: putItemList };
};

export const fetchInspectionTypeData = createAsyncThunk(
  'typeData/fetchTypeData',
  async (params: GetInspectionTypeParam, { rejectWithValue }) => {
    try {
      return await fetchTypeDataAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchQcTypeData = createAsyncThunk(
  'typeData/fetchQcTypeData',
  async (params: GetQcTypeParam, { rejectWithValue }) => {
    try {
      return await fetchQcType(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateInspectionTypeData = createAsyncThunk(
  'typeData/updateTypeData',
  async (params: RejectedTypeList, { rejectWithValue }) => {
    try {
      const dataToUpdate = convertPutData(params.newInspectionType);
      return params.isInspection
        ? await updateTypeDataAPI(dataToUpdate)
        : await updateQcType(dataToUpdate);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchQcTypeDataAll = createAsyncThunk(
  'typeData/fetchQcTypeDataAll',
  async (params: GetQcTypeAllParam, { rejectWithValue }) => {
    try {
      return await fetchQcTypeAllAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const findAndUpdateItem = (
  items: GetInspectionItem[],
  path: string[],
  updateFn: (item: GetInspectionItem) => void,
): boolean => {
  const [currentId, ...restPath] = path;
  for (const item of items) {
    if (item.id === currentId) {
      if (restPath.length === 0) {
        updateFn(item);
        return true;
      }
      if (
        item.itemData.length > 0 &&
        findAndUpdateItem(item.itemData, restPath, updateFn)
      ) {
        return true;
      }
    }
  }
  return false;
};

const findAndUpdateMarker = (
  markers: MarkerInfo[],
  markerId: number,
  updateFn: (marker: MarkerInfo) => void,
): boolean => {
  for (const marker of markers) {
    if (marker.id === markerId) {
      updateFn(marker);
      return true;
    }
  }
  return false;
};

const inspectionTypeDataSlice = createSlice({
  name: 'inspectionTypeData',
  initialState,
  reducers: {
    updateTypeMemo: (
      state,
      action: PayloadAction<{
        categoryId: string;
        typeId: string;
        memo: string;
      }>,
    ) => {
      const { categoryId, typeId, memo } = action.payload;
      if (!state.inspectionTypeDataMap[categoryId]) {
        return;
      }
      const typeData = state.inspectionTypeDataMap[categoryId][typeId];
      if (typeData) {
        typeData.memo = memo;
      }
    },
    updateMarker: (
      state,
      action: PayloadAction<{
        marker: MarkerInfo;
      }>,
    ) => {
      const { marker } = action.payload;
      const { id: markerId, x: newX, y: newY, memo: newMemo } = marker;

      const { categoryId, typeIdList } = DTA_IMAGE_INSPECTION;
      if (!state.inspectionTypeDataMap[categoryId]) {
        return;
      }
      // 全ての中項目データ間で整合性を保つ
      typeIdList.forEach((id) => {
        const typeData = state.inspectionTypeDataMap[categoryId][id];
        if (typeData) {
          if (!typeData.markers) {
            typeData.markers = [];
          }
          if (!typeData.markers[markerId]) {
            typeData.markers.push({
              id: markerId,
              x: newX,
              y: newY,
              memo: newMemo,
            });
            return;
          }
          findAndUpdateMarker(typeData.markers, markerId, (marker) => {
            marker.x = newX;
            marker.y = newY;
            marker.memo = newMemo;
          });
        }
      });
    },
    updateStatus: (
      state,
      action: PayloadAction<{
        categoryId: string;
        typeId: string;
        status: 0 | 1 | 2;
      }>,
    ) => {
      const { categoryId, typeId, status } = action.payload;
      if (
        !state.inspectionTypeDataMap[categoryId] ||
        !state.inspectionTypeDataMap[categoryId][typeId]
      ) {
        return;
      }
      state.inspectionTypeDataMap[categoryId][typeId].status = status;
    },
    updateJudgement: (
      state,
      action: PayloadAction<{
        categoryId: string;
        typeId: string;
        path: string[];
        judgement: number;
      }>,
    ) => {
      const { categoryId, typeId, path, judgement } = action.payload;
      if (!state.inspectionTypeDataMap[categoryId]) {
        return;
      }
      const typeData = state.inspectionTypeDataMap[categoryId][typeId];
      if (typeData) {
        findAndUpdateItem(typeData.itemData, path, (item) => {
          item.judgement = judgement;
        });
      }
    },
    updateNumericalValue: (
      state,
      action: PayloadAction<{
        categoryId: string;
        typeId: string;
        path: string[];
        numericalValue: number | null;
      }>,
    ) => {
      const { categoryId, typeId, path, numericalValue } = action.payload;
      if (!state.inspectionTypeDataMap[categoryId]) {
        return;
      }
      const typeData = state.inspectionTypeDataMap[categoryId][typeId];
      if (typeData) {
        findAndUpdateItem(typeData.itemData || [], path, (item) => {
          item.numericalValue = numericalValue;
        });
      }
    },
    updateMemo: (
      state,
      action: PayloadAction<{
        categoryId: string;
        typeId: string;
        path: string[];
        memo: string;
      }>,
    ) => {
      const { categoryId, typeId, path, memo } = action.payload;
      if (!state.inspectionTypeDataMap[categoryId]) {
        return;
      }
      const typeData = state.inspectionTypeDataMap[categoryId][typeId];
      if (typeData) {
        findAndUpdateItem(typeData.itemData || [], path, (item) => {
          item.memo = memo;
        });
      }
    },

    updateIsAllOk: (state) => {
      /** 指定したIDの項目が前回OKか */
      const isBeforeOk = (
        categoryId: string,
        typeId: string,
        itemId: string,
      ) => {
        if (!state.before[categoryId] || !state.before[categoryId][typeId]) {
          return false;
        }
        const beforeItem = findInspectionItem(
          state.before[categoryId][typeId].itemData,
          itemId,
        );

        return beforeItem && isOk(beforeItem.judgement);
      };

      // 全項目が「OK」or「今回N/Aで前回OK」かチェック(空の項目は無い前提)
      state.isAllOk = Object.entries(state.inspectionTypeDataMap).every(
        ([categoryId, typeMap]) => {
          return Object.entries(typeMap).every(([typeId, typeData]) => {
            return typeData.itemData.every((depth0) => {
              switch (typeData.displayType) {
                case INSPECTION_DISPLAY_TYPE.STANDARD.value:
                case INSPECTION_DISPLAY_TYPE.ACCORDION.value:
                  // 標準・アコーディオンは階層0の結果で判定
                  return (
                    isOk(depth0.judgement) ||
                    (isNa(depth0.judgement) &&
                      isBeforeOk(categoryId, typeId, depth0.id))
                  );
                case INSPECTION_DISPLAY_TYPE.MATRIX.value:
                  // マトリクスは階層1の結果で判定
                  return depth0.itemData.every((depth1) => {
                    return (
                      isOk(depth1.judgement) ||
                      (isNa(depth1.judgement) &&
                        isBeforeOk(categoryId, typeId, depth1.id))
                    );
                  });
              }
            });
          });
        },
      );
    },
    resetInspectionTypeData: (state) => {
      state.inspectionTypeDataMap = {};
      state.before = {};
      state.isAllOk = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInspectionTypeData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchInspectionTypeData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetInspectionType(action.payload)) {
          const { categoryId, typeId } = action.meta.arg;
          if (!state.inspectionTypeDataMap[categoryId]) {
            state.inspectionTypeDataMap[categoryId] = {};
          }
          const typeData: InspectionType = {
            ...action.payload,
            status: getStatusInspectionOfType(action.payload.itemData),
          };
          state.inspectionTypeDataMap[categoryId][typeId] = typeData;
        }
      })
      .addCase(fetchInspectionTypeData.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updateInspectionTypeData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateInspectionTypeData.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateInspectionTypeData.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchQcTypeData.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchQcTypeData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetQcType(action.payload)) {
          const { categoryId, typeId } = action.meta.arg;
          if (!state.inspectionTypeDataMap[categoryId]) {
            state.inspectionTypeDataMap[categoryId] = {};
          }
          if (!state.before[categoryId]) {
            state.before[categoryId] = {};
          }

          const typeData: InspectionType = {
            ...action.payload.after,
            status: getStatusInspectionOfType(action.payload.after.itemData),
          };
          state.inspectionTypeDataMap[categoryId][typeId] = typeData;
          state.before[categoryId][typeId] = action.payload.before;
        }
      })
      .addCase(fetchQcTypeData.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchQcTypeDataAll.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchQcTypeDataAll.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetQcTypeList(action.payload)) {
          action.payload.after.forEach((after) => {
            if (!state.inspectionTypeDataMap[after.categoryId]) {
              state.inspectionTypeDataMap[after.categoryId] = {};
            }
            const typeData: InspectionType = {
              ...after,
              status: getStatusInspectionOfType(after.itemData),
            };
            state.inspectionTypeDataMap[after.categoryId][after.typeId] =
              typeData;
          });
          action.payload.before.forEach((before) => {
            if (!state.before[before.categoryId]) {
              state.before[before.categoryId] = {};
            }

            state.before[before.categoryId][before.typeId] = before;
          });
        }
      })
      .addCase(fetchQcTypeDataAll.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const {
  updateTypeMemo,
  updateMarker,
  updateStatus,
  updateJudgement,
  updateNumericalValue,
  updateMemo,
  updateIsAllOk,
  resetInspectionTypeData,
} = inspectionTypeDataSlice.actions;
export default inspectionTypeDataSlice.reducer;
